<template>
    <v-card-text>
        <LoadingOverlay :model-value="!!resettingPasswordForId" />
        <v-alert v-if="employees.length === 0" type="warning">
            {{ $t("employeeView.noEmployees") }}
        </v-alert>
        <div v-else>
            <v-row>
                <v-col
                    v-for="employee in employees"
                    :key="employee.id"
                    cols="12"
                    sm="12"
                    md="12"
                    lg="6"
                >
                    <BulletCard
                        :cardTitle="getEmployeeCardTitle(employee)"
                        titleFontSize="20px"
                        :bullets="formatEmployee(employee)"
                        :onResetPassword="
                            canResetPassword(employee)
                                ? () => handleResetPassword(employee)
                                : null
                        "
                        :onDelete="
                            canDelete(employee)
                                ? () => openConfirmDelete(employee.id)
                                : null
                        "
                        :onEdit="
                            canEdit(employee)
                                ? () => goToEditEmployee(employee.id)
                                : null
                        "
                    />
                </v-col>
            </v-row>
        </div>
    </v-card-text>

    <ConfirmDialog
        :visible="showConfirmDialog"
        :title="$t('employeeView.confirmDeleteTitle')"
        :message="$t('employeeView.confirmDeleteMessage')"
        :primary-text="$t('common.delete')"
        :secondary-text="$t('common.cancel')"
        secondary-color="error"
        primary-color="error"
        @update:visible="onConfirmDeleteDialogVisible"
        @primary="confirmDeleteEmployee"
        @secondary="cancelDelete"
    />
</template>

<script>
import axios from "axios";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "@/firebase/config";
import BulletCard from "@/components/display/BulletCard.vue";
import ConfirmDialog from "@/components/display/ConfirmDialog.vue";
import LoadingOverlay from "@/components/display/LoadingOverlay.vue";
import { mapMutations } from "vuex";
import emitter from "@/eventBus";
import signalIcon from "@/assets/signal.svg";
import { showAlertModal } from "@/utils";

export default {
    name: "EmployeesView",
    components: { BulletCard, ConfirmDialog, LoadingOverlay },
    data() {
        return {
            resettingPasswordForId: null,
            languageToFlag: {
                ro: "Flag-Romania.svg",
                hu: "Flag-Hungary.svg",
                en: "Flag-Great Britain.svg",
                de: "Flag-Germany.svg",
                fr: "Flag-France.svg",
                bg: "Flag-Bulgaria.svg",
                pl: "Flag-Poland.svg",
                sr: "Flag-Serbia.svg",
                hr: "Flag-Croatia.svg",
                sl: "Flag-Slovenia.svg",
                sk: "Flag-Slovakia.svg",
                it: "Flag-Italy.svg",
            },
            employees: [],
            showConfirmDialog: false,
            selectedEmployeeId: null,
            currentUserRole: "",
            currentUserId: null,
        };
    },
    computed: {
        // …existing mapState + chips
        externalCompanyMode() {
            return Boolean(this.$route.params.companyId);
        },
    },
    created() {
        this.fetchEmployees();
    },
    mounted() {
        const userData = sessionStorage.getItem("user");
        if (userData) {
            const currentUser = JSON.parse(userData);
            this.currentUserRole = currentUser.role;
            this.currentUserId = currentUser.id;

            if (
                this.currentUserRole === "admin" &&
                !this.externalCompanyMode &&
                currentUser.company
            ) {
                this.SET_FORM_BUTTONS([
                    {
                        label: "employeeView.addEmployee",
                        event: "add-new-employee",
                        color: "primary",
                        dataCy: 'add-new-employee',
                    },
                ]);
            }
        }
        emitter.on("add-new-employee", this.goToAddEmployee);
    },
    unmounted() {
        emitter.off("add-new-employee", this.goToAddEmployee);
        this.RESET_FORM_BUTTONS();
    },
    methods: {
        ...mapMutations(["SET_FORM_BUTTONS", "RESET_FORM_BUTTONS"]),
        showAlertModal,
        getEmployeeCardTitle(employee) {
            const fullName = `${employee.first_name} ${employee.last_name}`;

            // Show role only for same-company views
            const isOwnCompanyView = !this.externalCompanyMode;

            // Only show the role if it's not "employee"
            if (isOwnCompanyView && employee.role !== "employee") {
                const localizedRole = this.$t(
                    `employeeView.role_${employee.role}`
                );
                return `${fullName} (${localizedRole})`;
            }

            return fullName;
        },
        canEdit(employee) {
            if (this.externalCompanyMode) return false;
            return (
                this.currentUserRole === "admin" ||
                (this.currentUserRole === "employee" &&
                    employee.id === this.currentUserId)
            );
        },
        canDelete(employee) {
            if (this.externalCompanyMode) return false;
            if (employee.id === this.currentUserId) return false;
            return this.currentUserRole === "admin";
        },
        canResetPassword(employee) {
            if (this.externalCompanyMode) return false;
            return (
                this.currentUserRole === "admin" ||
                (this.currentUserRole === "employee" && employee.id === this.currentUserId)
            );
        },
        async handleResetPassword(employee) {
            this.resettingPasswordForId = employee.id;
            try {
                const { data } = await axios.get(
                    `${process.env.VUE_APP_API_URL}/auth/auth-provider/`,
                    { params: { user_id: employee.id } }
                );
                if (data.provider === "firebase") {
                    await sendPasswordResetEmail(auth, data.email);
                } else {
                    const resetRes = await axios.post(
                        `${process.env.VUE_APP_API_URL}/auth/request-password-reset/`,
                        { email: data.email }
                    );
                    if (resetRes.data?.provider === "firebase") {
                        await sendPasswordResetEmail(auth, resetRes.data.email);
                    }
                }
                this.showAlertModal(
                    this.$store,
                    this.$t("resetRequest.sent"),
                    "success",
                    5000
                );
            } catch (e) {
                console.error("Reset password error:", e);
                this.showAlertModal(
                    this.$store,
                    this.$t("resetRequest.error"),
                    "danger",
                    12000,
                    e
                );
            } finally {
                this.resettingPasswordForId = null;
            }
        },
        /**
         * Fetches employees for the connected user's company,
         * filtering by the role "employee".
         */
        async fetchEmployees() {
            try {
                const userData = sessionStorage.getItem("user");
                if (!userData) {
                    console.error("No user data found in sessionStorage");
                    this.showAlertModal(
                        this.$store,
                        "No user data found in sessionStorage",
                        "danger",
                        12000
                    );
                    return;
                }

                const currentUser = JSON.parse(userData);
                const routeCompanyId = this.$route.params.companyId || null;

                const params = {};

                if (routeCompanyId) {
                    // Case 1: explicit company from route
                    params.company = routeCompanyId;
                    // role filter intentionally omitted
                } else if (currentUser.company) {
                    // Case 2 / 3: own company (admin OR employee)
                    params.company = currentUser.company;
                } else {
                    // user has no company → expect empty result
                    this.employees = [];
                    return;
                }

                const { data } = await axios.get(
                    `${process.env.VUE_APP_API_URL}/employees/`,
                    { params }
                );
                this.employees = data;
            } catch (error) {
                console.error("Error fetching employees:", error);
                this.showAlertModal(
                    this.$store,
                    "Error fetching employees",
                    "danger",
                    12000,
                    error
                );
            }
        },
        /**
         * Formats an employee's details into an array of bullet strings.
         * Returns the employee's role, email, and phone number.
         * @param {Object} employee - The employee object.
         * @returns {Array<string>} An array of strings to display as bullets.
         */
        formatEmployee(employee) {
            const t = this.$t.bind(this);

            /* ---------------- phone formatter ---------------- */
            const formatPhone = ({ prefix = "", number = "" } = {}) => {
                if (!number) return null;
                const normalized = prefix
                    ? prefix.startsWith("+")
                        ? prefix
                        : `+${prefix}`
                    : "";
                return normalized ? `${normalized} ${number}` : number;
            };

            /* ---------------- phones ---------------- */
            const phoneList = [];

            // primary
            const mainPhone = formatPhone({
                prefix: employee.phone_prefix,
                number: employee.phone,
            });
            if (mainPhone) phoneList.push(mainPhone);

            // extras
            (employee.phones || []).forEach((p) => {
                const extra = formatPhone(p);
                if (extra) phoneList.push(extra);
            });

            const phonesLine = phoneList.length
                ? phoneList.join(", ")
                : t("employeeView.phoneNotAvailable");

            /* ---------------- e-mails ---------------- */
            const emailList = [];

            if (employee.email) emailList.push(employee.email);
            (employee.alt_emails || []).forEach((e) => {
                if (e.email) emailList.push(e.email);
            });

            const emailsLine = emailList.length
                ? emailList
                      .map((e) => `<a href="mailto:${e}">${e}</a>`)
                      .join(", ")
                : t("company.nA");

            /* ---------------- build bullets ---------------- */
            const bullets = [
                `${t("addEmployee.jobTitle")}: ${
                    employee.job_title
                        ? this.$te(`jobTitle.${employee.job_title}`)
                            ? t(`jobTitle.${employee.job_title}`)
                            : employee.job_title
                        : t("employeeView.unspecified")
                }`,
                `${t("employeeView.email")}: ${emailsLine}`,
                `${t("employeeView.phone")}: ${phonesLine}`,
                `${t("addEmployee.truckEmails")}: ${
                    employee.should_receive_truck_emails
                        ? t("common.yes")
                        : t("common.no")
                }`,
                `${t("addEmployee.wareEmails")}: ${
                    employee.should_receive_ware_emails
                        ? t("common.yes")
                        : t("common.no")
                }`,
            ];

            /* ---------------- Signal / WhatsApp icons ---------------- */
            if (employee.hasSignalApp || employee.hasWhatsApp) {
                const icons = [];
                if (employee.hasSignalApp) {
                    icons.push(
                        `<img src="${signalIcon}" alt="Signal" style="width:20px;height:20px;vertical-align:middle;">`
                    );
                }
                if (employee.hasWhatsApp) {
                    icons.push(
                        `<span class="mdi mdi-whatsapp" style="color:green;font-size:20px;vertical-align:middle;"></span>`
                    );
                }
                bullets.push(
                    `${t("addEmployee.canBeContactedOn")} ${icons.join(" ")}`
                );
            }

            /* ---------------- spoken languages ---------------- */
            if (employee.spoken_languages?.length) {
                const flags = employee.spoken_languages
                    .map(
                        (
                            lang
                        ) => `<img src="${this.$baseUrl}country-flags/${this.languageToFlag[lang]}"
                     alt="${lang}"
                     style="width:24px;height:24px;margin-left:6px;vertical-align:middle;">`
                    )
                    .join("");
                bullets.push(
                    `<strong>${t("entries.speaks")}:</strong> ${flags}`
                );
            }

            return bullets;
        },
        /**
         * Navigates to the add-employee page.
         */
        goToAddEmployee() {
            this.$router.push({ name: "AddEmployee" });
        },
        /**
         * Navigates to the add-employee page but with
         * an employeeId param so we can edit that employee.
         */
        goToEditEmployee(employeeId) {
            this.$router.push({
                name: "AddEmployee",
                params: { id: employeeId },
            });
        },
        /**
         * Opens a confirmation dialog for deleting an employee.
         */
        openConfirmDelete(employeeId) {
            this.selectedEmployeeId = employeeId;
            this.showConfirmDialog = true;
        },
        /**
         * Cancels the deletion by hiding the dialog.
         */
        cancelDelete() {
            this.showConfirmDialog = false;
            this.selectedEmployeeId = null;
        },
        /** Sync dialog visibility (e.g. overlay close) with local state. */
        onConfirmDeleteDialogVisible(isVisible) {
            if (!isVisible) this.cancelDelete();
        },
        /**
         * Confirms deletion of the selected employee.
         */
        async confirmDeleteEmployee() {
            if (!this.selectedEmployeeId) return;

            try {
                await axios.delete(
                    `${process.env.VUE_APP_API_URL}/employees/${this.selectedEmployeeId}/`
                );
                this.employees = this.employees.filter(
                    (e) => e.id !== this.selectedEmployeeId
                );
            } catch (error) {
                console.error("Error deleting employee:", error);
                this.showAlertModal(
                    this.$store,
                    "Error deleting employee",
                    "danger",
                    12000,
                    error
                );
            } finally {
                // Regardless of success/failure, close dialog
                this.cancelDelete();
            }
        },
    },
};
</script>

<style scoped>
/* Component-specific styles */
</style>
